<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import {
  formatCurrencyInput,
  parseCurrencyInput,
} from 'dashboard/components-next/input/inputUtils';
import { paymentOptions } from '../composables/salesPropertyFormOptions';
import SalesPropertyReferenceField from './SalesPropertyReferenceField.vue';
import SalesPropertyWarningAlert from './SalesPropertyWarningAlert.vue';

const props = defineProps({
  profile: { type: Object, required: true },
  errors: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['update']);
const { t } = useI18n();
const options = computed(() => paymentOptions(t));
const fgtsWarningVisible = ref(false);

const selectedPayments = computed(() => props.profile.forma_pagamento || []);
const paymentDisabled = value => {
  if (selectedPayments.value.includes(value)) return false;
  if (!['1', '2', '4'].includes(value)) return false;
  return selectedPayments.value.some(item => ['1', '2', '4'].includes(item));
};

const update = (field, value) => emit('update', field, value);
const moneyValue = value => {
  if (value === null || value === undefined || value === '') return '';
  const source = String(value);
  const parsed = /[R$,]/.test(source)
    ? parseCurrencyInput(source)
    : Number(source);
  return parsed === null || Number.isNaN(parsed)
    ? ''
    : formatCurrencyInput(parsed);
};
const updateMoney = (field, value) =>
  update(field, parseCurrencyInput(value) ?? '');
const togglePayment = value => {
  if (paymentDisabled(value)) return;
  const next = selectedPayments.value.includes(value)
    ? selectedPayments.value.filter(item => item !== value)
    : [...selectedPayments.value, value];
  fgtsWarningVisible.value = next.includes('3');
  update('forma_pagamento', next);
};
</script>

<template>
  <LaisCard
    density="flush"
    class="self-start !border-0 !shadow-sm"
    data-test="sales-property-values-section"
  >
    <div class="grid gap-5 p-8 font-roboto">
      <h2
        class="mb-[2px] font-lato text-[32px] font-bold leading-[39px] text-[#464F53] max-[700px]:mb-6 max-[700px]:text-[24px] max-[700px]:leading-[29px]"
      >
        {{ t('SALES_PROPERTY.VALUES.TITLE') }}
      </h2>
      <div
        data-test="sales-property-value-fields"
        class="grid gap-8 min-[701px]:grid-cols-3 min-[701px]:gap-4"
      >
        <SalesPropertyReferenceField
          :filled="Boolean(props.profile.valorAnunciado)"
          :label="`${t('SALES_PROPERTY.VALUES.ANNOUNCED')}*`"
          :error="errors.valorAnunciado"
          required
        >
          <input
            :value="moneyValue(props.profile.valorAnunciado)"
            :placeholder="t('SALES_PROPERTY.VALUES.MONEY_PLACEHOLDER')"
            inputmode="decimal"
            class="h-8 w-full border-0 border-b border-[#009F94] bg-transparent px-0 text-base font-bold text-[#009F94] outline-none placeholder:text-[#A7B7BE]"
            @input="updateMoney('valorAnunciado', $event.target.value)"
          />
        </SalesPropertyReferenceField>
        <SalesPropertyReferenceField
          :filled="Boolean(props.profile.valorVenda)"
          :label="t('SALES_PROPERTY.VALUES.SALE')"
          :error="errors.valorVenda"
          required
        >
          <input
            :value="moneyValue(props.profile.valorVenda)"
            :placeholder="t('SALES_PROPERTY.VALUES.MONEY_PLACEHOLDER')"
            inputmode="decimal"
            class="h-8 w-full border-0 border-b border-[#009F94] bg-transparent px-0 text-base font-bold text-[#009F94] outline-none placeholder:text-[#A7B7BE]"
            @input="updateMoney('valorVenda', $event.target.value)"
          />
        </SalesPropertyReferenceField>
        <SalesPropertyReferenceField
          :filled="Boolean(props.profile.valorSinal)"
          :label="t('SALES_PROPERTY.VALUES.DOWN_PAYMENT')"
          :error="errors.valorSinal"
          required
        >
          <input
            :value="moneyValue(props.profile.valorSinal)"
            :placeholder="t('SALES_PROPERTY.VALUES.MONEY_PLACEHOLDER')"
            inputmode="decimal"
            class="h-8 w-full border-0 border-b border-[#009F94] bg-transparent px-0 text-base font-bold text-[#009F94] outline-none placeholder:text-[#A7B7BE]"
            @input="updateMoney('valorSinal', $event.target.value)"
          />
        </SalesPropertyReferenceField>
      </div>
      <fieldset
        class="mt-[27px] grid gap-0 max-[700px]:mt-3"
        :aria-label="t('SALES_PROPERTY.ARIA.PAYMENT')"
      >
        <legend class="mb-5 text-xl leading-6 text-[#464F53]">
          {{ t('SALES_PROPERTY.VALUES.PAYMENT_TITLE') }}
        </legend>
        <div
          data-test="sales-property-payment-options"
          class="flex flex-col gap-0"
        >
          <label
            v-for="option in options"
            :key="option.value"
            class="mb-2 flex min-h-9 self-start items-center gap-2 rounded-[10px] px-3 text-base text-[#464F53]"
            :class="[
              paymentDisabled(option.value) ? 'opacity-50' : '',
              selectedPayments.includes(option.value)
                ? 'bg-[#EDF8F7] !text-[#01988C]'
                : '',
            ]"
          >
            <span class="relative grid size-[18px] shrink-0 place-items-center">
              <input
                type="checkbox"
                :checked="selectedPayments.includes(option.value)"
                :disabled="paymentDisabled(option.value)"
                class="peer sr-only"
                @change="togglePayment(option.value)"
              />
              <span
                class="size-[18px] rounded-[2px] border-[1.5px] border-[#01988C] bg-white peer-checked:bg-[#01988C]"
              />
              <span
                v-if="selectedPayments.includes(option.value)"
                class="i-lucide-check pointer-events-none absolute size-3 text-white"
                aria-hidden="true"
              />
            </span>
            {{ option.label }}
          </label>
        </div>
        <p v-if="errors.forma_pagamento" class="text-xs text-lais-danger">
          {{ errors.forma_pagamento }}
        </p>
      </fieldset>
      <SalesPropertyWarningAlert
        v-if="fgtsWarningVisible"
        test-id="values-fgts-warning"
        :dismiss-label="t('SALES_PROPERTY.VALUES.DISMISS_WARNING')"
        @dismiss="fgtsWarningVisible = false"
      >
        {{ t('SALES_PROPERTY.VALUES.FGTS_WARNING') }}
      </SalesPropertyWarningAlert>
      <div class="mt-[30px] mb-[20px]">
        <SalesPropertyReferenceField
          :filled="Boolean(props.profile.observacaoPagamento)"
          :label="t('SALES_PROPERTY.VALUES.PAYMENT_NOTES')"
          :show-success="false"
          class="!min-h-[118px] max-[700px]:!min-h-[146px]"
        >
          <textarea
            :value="props.profile.observacaoPagamento"
            :placeholder="t('SALES_PROPERTY.VALUES.PAYMENT_NOTES_PLACEHOLDER')"
            rows="2"
            class="h-10 w-full resize-none border-0 border-b border-[#009F94] bg-transparent px-0 text-base leading-5 text-[#009F94] outline-none placeholder:text-[#A7B7BE]"
            @input="update('observacaoPagamento', $event.target.value)"
          />
        </SalesPropertyReferenceField>
      </div>
      <slot name="footer" />
    </div>
  </LaisCard>
</template>
